// Booking step for collecting participant details (full name + passport number)
// Used for tours that require park confirmation (e.g., Corcovado National Park)

import React, { useState } from 'react';
import { FileText, User, AlertCircle } from 'lucide-react';
import { Tour } from '../../types/tours';
import { BookingParticipant } from '../../types/bookings';

interface BookingParticipantsStepProps {
  tour: Tour;
  persons: number;
  participants: BookingParticipant[];
  onChange: (participants: BookingParticipant[]) => void;
  onBack: () => void;
  onNext: () => void;
}

const BookingParticipantsStep: React.FC<BookingParticipantsStepProps> = ({
  tour,
  persons,
  participants,
  onChange,
  onBack,
  onNext
}) => {
  const [error, setError] = useState<string | null>(null);

  const ensureParticipantSlots = (): BookingParticipant[] => {
    const current = [...participants];
    while (current.length < persons) {
      current.push({ full_name: '', passport_number: '', display_order: current.length });
    }
    return current.slice(0, persons);
  };

  const currentParticipants = ensureParticipantSlots();

  const handleChange = (index: number, field: 'full_name' | 'passport_number', value: string) => {
    const updated = [...currentParticipants];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const handleNext = () => {
    setError(null);

    for (let i = 0; i < currentParticipants.length; i++) {
      if (!currentParticipants[i].full_name.trim()) {
        setError(`Please enter the full name for participant ${i + 1}`);
        return;
      }
      if (!currentParticipants[i].passport_number.trim()) {
        setError(`Please enter the passport number for participant ${i + 1}`);
        return;
      }
    }

    onChange(currentParticipants);
    onNext();
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">Participant Details</h2>
        <p className="text-[#2A382A]/70">
          Corcovado National Park requires the full name and passport number of each visitor. 
          This information is mandatory for park entry.
        </p>
      </div>

      {/* Info Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-800">
          <p className="font-medium mb-1">Why do we need this?</p>
          <p>The park has a daily visitor limit. We submit your details to reserve your spot. You will receive confirmation within 24 hours.</p>
        </div>
      </div>

      {/* Participant Fields */}
      <div className="space-y-6">
        {currentParticipants.map((participant, index) => (
          <div key={index} className="bg-[#F2F3F4] rounded-xl p-6">
            <h3 className="font-semibold text-[#2A382A] mb-4 flex items-center gap-2">
              <div className="w-7 h-7 bg-[#2A382A] rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-bold">{index + 1}</span>
              </div>
              Participant {index + 1}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2A382A]/70 mb-2">
                  Full Name (as on passport)
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A382A]/40" />
                  <input
                    type="text"
                    value={participant.full_name}
                    onChange={(e) => handleChange(index, 'full_name', e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
                    placeholder="John Michael Doe"
                    autoFocus={index === 0}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2A382A]/70 mb-2">
                  Passport Number
                </label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2A382A]/40" />
                  <input
                    type="text"
                    value={participant.passport_number}
                    onChange={(e) => handleChange(index, 'passport_number', e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-white border border-[#2A382A]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-[#2A382A]"
                    placeholder="AB1234567"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-2 bg-white border border-[#2A382A]/10 text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          className="px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default BookingParticipantsStep;
